import React, { useState } from 'react';
import { ArrowLeft, Plus, Search, Filter, Calendar, FileText, DollarSign, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface Imposto {
  id: string;
  tipo: 'irpf' | 'inss' | 'municipal' | 'estadual' | 'federal' | 'outros';
  descricao: string;
  valor: number;
  dataVencimento: string;
  dataPagamento?: string;
  status: 'pago' | 'pendente' | 'atrasado';
  numeroDocumento?: string;
  responsavel: string;
  observacoes?: string;
}

const ImpostosPage: React.FC = () => {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState({
    tipo: '',
    descricao: '',
    status: '',
    dataInicio: '',
    dataFim: ''
  });

  // Dados mockados
  const [impostos, setImpostos] = useState<Imposto[]>([
    {
      id: '1',
      tipo: 'irpf',
      descricao: 'Imposto de Renda 2024',
      valor: 2500.00,
      dataVencimento: '2024-04-30',
      dataPagamento: '2024-04-25',
      status: 'pago',
      numeroDocumento: 'DARF-2024-001',
      responsavel: 'Contador Silva',
      observacoes: 'Declaração anual'
    },
    {
      id: '2',
      tipo: 'municipal',
      descricao: 'IPTU Igreja',
      valor: 1200.00,
      dataVencimento: '2024-12-31',
      status: 'pendente',
      numeroDocumento: 'IPTU-2024-456',
      responsavel: 'Tesoureiro João',
      observacoes: 'Imposto predial'
    },
    {
      id: '3',
      tipo: 'inss',
      descricao: 'INSS Funcionários Nov/2024',
      valor: 850.00,
      dataVencimento: '2024-11-20',
      status: 'atrasado',
      numeroDocumento: 'GPS-2024-789',
      responsavel: 'RH Maria',
      observacoes: 'Contribuição previdenciária'
    }
  ]);

  const statusOrder = ['pendente', 'pago', 'atrasado'];

  const handleStatusClick = (id: string) => {
    setImpostos(prev => prev.map(imposto => {
      if (imposto.id === id) {
        const currentIndex = statusOrder.indexOf(imposto.status);
        const nextIndex = (currentIndex + 1) % statusOrder.length;
        return { ...imposto, status: statusOrder[nextIndex] as 'pago' | 'pendente' | 'atrasado' };
      }
      return imposto;
    }));
  };

  const totalImpostos = impostos.reduce((acc, imp) => acc + imp.valor, 0);
  const impostosPendentes = impostos.filter(imp => imp.status === 'pendente').length;
  const impostosAtrasados = impostos.filter(imp => imp.status === 'atrasado').length;
  const impostosPagos = impostos.filter(imp => imp.status === 'pago').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pago': return 'bg-green-500';
      case 'pendente': return 'bg-yellow-500';
      case 'atrasado': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pago': return 'Pago';
      case 'pendente': return 'Pendente';
      case 'atrasado': return 'Atrasado';
      default: return status;
    }
  };

  const getTipoText = (tipo: string) => {
    switch (tipo) {
      case 'irpf': return 'IRPF';
      case 'inss': return 'INSS';
      case 'municipal': return 'Municipal';
      case 'estadual': return 'Estadual';
      case 'federal': return 'Federal';
      case 'outros': return 'Outros';
      default: return tipo;
    }
  };

  // Filtrar impostos
  const impostosFiltrados = impostos.filter(imposto => {
    const matchTipo = !filtros.tipo || filtros.tipo === 'all' || imposto.tipo === filtros.tipo;
    const matchDescricao = !filtros.descricao || imposto.descricao.toLowerCase().includes(filtros.descricao.toLowerCase());
    const matchStatus = !filtros.status || filtros.status === 'all' || imposto.status === filtros.status;
    
    let matchData = true;
    if (filtros.dataInicio && filtros.dataFim) {
      const dataVencimento = new Date(imposto.dataVencimento);
      const dataInicio = new Date(filtros.dataInicio);
      const dataFim = new Date(filtros.dataFim);
      matchData = dataVencimento >= dataInicio && dataVencimento <= dataFim;
    }
    
    return matchTipo && matchDescricao && matchStatus && matchData;
  });

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/financas/administracao')}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Impostos</h1>
              <p className="text-muted-foreground">Gestão de impostos e obrigações tributárias</p>
            </div>
          </div>
          <Button onClick={() => navigate('/financas/administracao/impostos/cadastrar')} className="bg-primary hover:bg-primary/90">
            <Plus className="h-4 w-4 mr-2" />
            Cadastrar Imposto
          </Button>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total de Impostos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-red-500 mr-2" />
                <span className="text-2xl font-bold text-red-500">
                  R$ {totalImpostos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Pagos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <FileText className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-2xl font-bold text-green-500">{impostosPagos}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-yellow-500 mr-2" />
                <span className="text-2xl font-bold text-yellow-500">{impostosPendentes}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Atrasados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
                <span className="text-2xl font-bold text-red-500">{impostosAtrasados}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros e Tabela de Impostos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtros e Registros de Impostos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Filtros */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="filtro-tipo">Tipo</Label>
                  <Select value={filtros.tipo} onValueChange={(value) => setFiltros({...filtros, tipo: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos os tipos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os tipos</SelectItem>
                      <SelectItem value="irpf">IRPF</SelectItem>
                      <SelectItem value="inss">INSS</SelectItem>
                      <SelectItem value="municipal">Municipal</SelectItem>
                      <SelectItem value="estadual">Estadual</SelectItem>
                      <SelectItem value="federal">Federal</SelectItem>
                      <SelectItem value="outros">Outros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filtro-descricao">Descrição</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      id="filtro-descricao"
                      placeholder="Buscar descrição..."
                      value={filtros.descricao}
                      onChange={(e) => setFiltros({...filtros, descricao: e.target.value})}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="filtro-status">Status</Label>
                  <Select value={filtros.status} onValueChange={(value) => setFiltros({...filtros, status: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos os status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os status</SelectItem>
                      <SelectItem value="pago">Pago</SelectItem>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="atrasado">Atrasado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="filtro-data-inicio">Data Inicial</Label>
                  <Input
                    id="filtro-data-inicio"
                    type="date"
                    value={filtros.dataInicio}
                    onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="filtro-data-fim">Data Final</Label>
                  <Input
                    id="filtro-data-fim"
                    type="date"
                    value={filtros.dataFim}
                    onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
                  />
                </div>
              </div>

              {/* Tabela */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">TIPO</th>
                       <th className="text-left py-3 px-4 font-medium text-muted-foreground">DESCRIÇÃO</th>
                       <th className="text-left py-3 px-4 font-medium text-muted-foreground">VALOR</th>
                       <th className="text-left py-3 px-4 font-medium text-muted-foreground">VENCIMENTO</th>
                       <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                         STATUS
                       </th>
                       <th className="text-left py-3 px-4 font-medium text-muted-foreground">RESPONSÁVEL</th>
                    </tr>
                  </thead>
                  <tbody>
                     {impostosFiltrados.map((imposto) => (
                       <tr key={imposto.id} className="border-b">
                         <td className="py-3 px-4">
                           <Badge variant="outline" className="text-blue-400 border-blue-400">
                             {getTipoText(imposto.tipo)}
                           </Badge>
                         </td>
                         <td className="py-3 px-4">{imposto.descricao}</td>
                         <td className="py-3 px-4 font-medium">
                           R$ {imposto.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                         </td>
                         <td className="py-3 px-4">
                           <div className="flex items-center">
                             <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
                             <span>
                               {new Date(imposto.dataVencimento).toLocaleDateString('pt-BR')}
                             </span>
                           </div>
                         </td>
                         <td className="py-3 px-4">
                           <Badge 
                             className={`cursor-pointer relative group ${getStatusColor(imposto.status)}`}
                             onClick={(e) => {
                               e.preventDefault();
                               e.stopPropagation();
                               handleStatusClick(imposto.id);
                             }}
                             title="Clique para mudar status"
                           >
                             {getStatusText(imposto.status)}
                             <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                               Clique para mudar status
                             </span>
                           </Badge>
                         </td>
                         <td className="py-3 px-4 text-muted-foreground">{imposto.responsavel}</td>
                       </tr>
                     ))}
                   </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Modal de Cadastro */}
        {/* Removido - agora usando card inline */}
      </div>
    </div>
  );
};

export default ImpostosPage;
export { ImpostosPage };